import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import SiteFormFields from "./SiteFormFields";
import RequiredFieldMarker from "./RequiredFieldMarker";
import studyService from "../../../services/studyService";

const SiteForm = ({
  onSubmit,
  isLoading,
  initialValues = {},
  title,
  mode = "add", // 'add' or 'edit'
}) => {
  const { studyID } = useParams();
  const [studyName, setStudyName] = useState("");
  const [values, setValues] = useState({
    site_code: initialValues.site_code || "",
    institution: initialValues.institution || "",
    country: initialValues.country || "United States",
    contact_person: initialValues.contact_person || "",
    email: initialValues.email || "",
    timezone: initialValues.timezone || "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!title && studyID) {
      studyService
        .getStudies()
        .then((studies) => {
          const study = studies.find((s) => s.id === studyID);
          if (study) setStudyName(study.title);
        })
        .catch(() => toast.error("Failed to load study info."));
    }
  }, [studyID, title]);

  const validate = () => {
    const newErrors = {};

    if (!/^\d{4}$/.test(values.site_code)) {
      newErrors.site_code = "Site code must be a 4-digit number";
    }

    if (!values.institution.trim()) {
      newErrors.institution = "Institution is required.";
    }

    if (!values.country.trim()) {
      newErrors.country = "Country is required.";
    }

    if (!values.contact_person.trim() || !values.contact_person.includes(" ")) {
      newErrors.contact_person = "Full name required.";
    }

    const strictEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!strictEmailRegex.test(values.email)) {
      newErrors.email = "Enter a valid email address.";
    }

    if (!values.timezone) {
      newErrors.timezone = "Timezone is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please correct all errors before submitting.");
      return;
    }
    onSubmit(values);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="mb-1"
      >
        <Link to={`/studies/${studyID}#sites`} className="back-link">
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Study</span>
        </Link>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="card overflow-hidden"
      >
        <div className="card-band">
          <h2 className="card-band-title">
            {title || (studyName ? `Add New Site to Study: ${studyName}` : 'Add New Site')}
          </h2>
        </div>

        <div className="p-6">
          <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
            Fields marked with <RequiredFieldMarker /> are required
          </p>

          {/* Live site name preview */}
          {values.site_code && values.institution && (
            <div className="mb-6">
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                Site {values.site_code} - {values.institution}
              </h3>
              <div className="h-1 w-20 rounded-full bg-primary-600 dark:bg-primary-500"></div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <SiteFormFields
              values={values}
              errors={errors}
              onChange={handleChange}
              mode={mode}
            />

            <div className="flex justify-end gap-3 border-t border-gray-100 pt-6 dark:border-gray-700">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary"
              >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {mode === "edit" ? "Save Changes" : "Add Site"}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default SiteForm;
